import { useCallback, useEffect, useRef, useState } from 'react';
import type { Notification, NotificationList } from '@mvcp/contracts';
import { getNotifications, markNotificationRead } from '../api';
import type { DrillTarget } from './CockpitShell';
import { Icon } from './Icon';
import { StatusPill } from './statusPill';
import { useDialogA11y } from './useDialogA11y';

type BellState =
  | { kind: 'loading' }
  | { kind: 'ready'; list: NotificationList }
  | { kind: 'error'; message: string };

/** The badge derivation — a known count, or "unavailable" (first-load failure, no last-known). */
type Badge = { kind: 'count'; unread: number } | { kind: 'unavailable' };

/**
 * Derive the bell badge. A failed REFETCH keeps the last-known count + dot (never a
 * false all-clear); a first-load failure with NO last-known count shows an
 * `unavailable` marker distinct from both "0 unread" and a normal unread dot.
 */
function computeBadge(state: BellState, lastGood: NotificationList | null): Badge {
  if (state.kind === 'ready') return { kind: 'count', unread: state.list.unreadCount };
  if (lastGood) return { kind: 'count', unread: lastGood.unreadCount };
  // loading with no history → show a benign 0 while the first fetch is in flight;
  // error with no history → the honest "unavailable" affordance.
  return state.kind === 'error' ? { kind: 'unavailable' } : { kind: 'count', unread: 0 };
}

/**
 * The topbar notification center (Story 4.5, FR-25). Because v1 sends NO expiry
 * emails, this bell is the sole in-app safety net for at-risk items. It is an
 * icon-only `<button>` whose accessible name carries the unread count (or an
 * "unavailable" affordance when it cannot load), plus a dropdown listing EVERY
 * current at-risk item (expiring / expired-missing / unverified) — LIVE-derived on
 * the server from the compliance engine, never a cache or the lagging ledger. The
 * badge fetches on mount; each open refetches so the list/count are always current
 * (freshness NFR-3). Clicking an item drills to its Vendor/Device record (whose
 * Certificate leaf shows the item, Story 4.4) and marks it read (server upsert),
 * which only dims the unread dot.
 */
export function NotificationBell({
  onOpenRecord,
}: {
  onOpenRecord: (target: DrillTarget) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<BellState>({ kind: 'loading' });
  const panelRef = useRef<HTMLDivElement>(null);
  // The last successfully-loaded list. A failed refetch falls back to this so the
  // badge never zeroes to a false all-clear; the optimistic-read path keeps it in
  // sync so a reverted read restores the true count.
  const lastGoodRef = useRef<NotificationList | null>(null);
  // Request-sequence guard: a slow stale response must never overwrite a newer one
  // on rapid open/close/open.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myId = (reqIdRef.current += 1);
    setState({ kind: 'loading' });
    try {
      const list = await getNotifications();
      if (myId !== reqIdRef.current) return; // superseded by a newer load
      // Guard a malformed/empty-body envelope so a bad payload surfaces the error
      // state instead of crashing the whole shell (the bell lives in the topbar).
      if (!list || !Array.isArray(list.notifications)) {
        throw new Error('Malformed notifications response');
      }
      lastGoodRef.current = list;
      setState({ kind: 'ready', list });
    } catch (err: unknown) {
      if (myId !== reqIdRef.current) return; // superseded by a newer load
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  // Fetch on mount for the badge count.
  useEffect(() => {
    void load();
  }, [load]);

  const badge = computeBadge(state, lastGoodRef.current);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      // Refetch on each OPEN so counts/items are current (never a stale cache).
      if (!wasOpen) void load();
      return !wasOpen;
    });
  }, [load]);

  /** Flip one notification's read flag in both the live state and the last-good cache. */
  const applyRead = useCallback((key: string, read: boolean) => {
    const cur = lastGoodRef.current;
    if (!cur) return;
    const notifications = cur.notifications.map((x) => (x.key === key ? { ...x, read } : x));
    const unreadCount = notifications.reduce((c, x) => (x.read ? c : c + 1), 0);
    const list: NotificationList = { notifications, unreadCount };
    lastGoodRef.current = list;
    setState((s) => (s.kind === 'ready' ? { kind: 'ready', list } : s));
  }, []);

  const onSelect = useCallback(
    (n: Notification) => {
      // Optimistic local read: dim the dot + drop the count immediately.
      applyRead(n.key, true);
      // Persist read-state. On failure, REVERT the optimistic read so the badge does
      // not silently under-count until the next refetch — but the drill + close STILL
      // happen regardless (navigation is never blocked by a mark-read failure).
      void markNotificationRead(n.key).catch(() => applyRead(n.key, false));
      // One action reaches the record.
      onOpenRecord({ kind: n.subjectType, id: n.subjectId });
      close();
    },
    [applyRead, onOpenRecord, close],
  );

  return (
    <div className="notification-bell-wrap">
      <button
        type="button"
        className="icon-button notification-bell"
        data-icon-only="true"
        aria-label={
          badge.kind === 'unavailable' ? 'Notifications, unavailable' : `Notifications, ${badge.unread} unread`
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        {/* Was a `🔔` emoji — the app's only glyph, and precisely what DESIGN.md bans
            ("no emoji, no icon font"). An emoji is a colour font: it painted its own palette
            and so never followed `--text-2` across the light/dark switch every other topbar
            control obeys, and it drew differently on every OS. The accessible name lives on
            the BUTTON (unread count / "unavailable"), so the glyph itself stays silent —
            several tests assert that name, and the dot/alert markers below are unchanged. */}
        <Icon name="bell" size={18} />
        {badge.kind === 'count' && badge.unread > 0 && (
          <span className="notification-bell__dot" aria-hidden="true" />
        )}
        {badge.kind === 'unavailable' && (
          <span className="notification-bell__alert" aria-hidden="true">
            !
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-catcher scrim: honours the dialog/aria-modal claim (page behind is
              not interactive) and dismisses on outside click. Mirrors Drawer.tsx. */}
          <div className="notification-scrim" role="presentation" onClick={close} />
          <NotificationDropdown
            panelRef={panelRef}
            state={state}
            onClose={close}
            onRetry={() => void load()}
            onSelect={onSelect}
          />
        </>
      )}
    </div>
  );
}

function NotificationDropdown({
  panelRef,
  state,
  onClose,
  onRetry,
  onSelect,
}: {
  panelRef: React.RefObject<HTMLDivElement | null>;
  state: BellState;
  onClose: () => void;
  onRetry: () => void;
  onSelect: (n: Notification) => void;
}): React.JSX.Element {
  // The shared overlay contract: Esc closes, initial focus into the panel, focus
  // returns to the bell on close, Tab is trapped within the panel (UX-DR-A11Y).
  useDialogA11y(panelRef, onClose);

  return (
    <div
      ref={panelRef}
      className="notification-panel card"
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
      tabIndex={-1}
    >
      <div className="notification-panel__head">
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>Notifications</h2>
        {/* One of the three 38px icon buttons DESIGN.md names outright ("theme toggle, bell,
            close"). The visible word "Close" is gone, so WCAG 2.5.3 Label in Name no longer
            constrains the name — but `aria-label` must stay EXACTLY "Close notifications",
            because it is now the control's only name and both the unit test and the UAT sweep
            click it by that string. */}
        <button
          type="button"
          className="icon-button"
          data-icon-only="true"
          onClick={onClose}
          aria-label="Close notifications"
        >
          <Icon name="close" size={17} />
        </button>
      </div>

      {state.kind === 'loading' && (
        <p className="muted" style={{ margin: 0 }} aria-busy="true">
          Loading…
        </p>
      )}

      {state.kind === 'error' && (
        <div className="stack" role="alert">
          <p className="muted" style={{ margin: 0 }}>
            Couldn’t load notifications. {state.message}
          </p>
          <button type="button" className="btn-accent" onClick={onRetry} style={{ alignSelf: 'flex-start' }}>
            Retry
          </button>
        </div>
      )}

      {state.kind === 'ready' &&
        (state.list.notifications.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            You’re all caught up
          </p>
        ) : (
          <ul className="notification-list">
            {state.list.notifications.map((n) => (
              <li key={n.key}>
                <button
                  type="button"
                  className="notification-item"
                  data-testid={`notification-${n.key}`}
                  onClick={() => onSelect(n)}
                  aria-label={`${n.label} — ${n.detail}${n.read ? '' : ' (unread)'}`}
                >
                  {/* Unread indicator is never color-alone: a visible dot PLUS the
                      "(unread)" in the accessible name above. */}
                  <span
                    className="notification-item__dot"
                    data-unread={n.read ? undefined : 'true'}
                    aria-hidden="true"
                  />
                  <StatusPill status={n.status} />
                  <span className="notification-item__body">
                    <span style={{ fontWeight: 600 }}>{n.label}</span>{' '}
                    <span className="muted" style={{ fontSize: 13 }}>
                      {n.detail}
                    </span>
                  </span>
                  {/* The framework code sits LAST in the DOM — after the body — because that is
                      the reading order, and it is what the `aria-label` above restates. Its
                      two-row placement, mono face and `white-space: nowrap` all live in
                      `.notification-item__code`: as a shrinkable inline-styled flex item it used
                      to break `IACS-E27` across two lines. */}
                  {n.frameworkCode && (
                    <span className="notification-item__code" data-numeric>
                      {n.frameworkCode}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}
